import { ReactElement, ReactNode, useState } from 'react';
import { Box, styled, Drawer, Typography, Skeleton, Divider } from '@mui/material';
import { CabAvatar, CabIcon, CabTooltip } from '@CabComponents';
import colors from '../../../colors';
import { BookingSlot, PublicLeader } from '../../../store';
import { DateTime } from 'luxon';
import { IoLocation, IoVideocam } from 'react-icons/io5';
import { NAVY_LOGO_SRC } from '../../../resourceUrls';


const SIDE_PANEL_WIDTH = '20vw';

const DEFAULT_LEADERS_TO_SHOW = 3;

const DrawerContent = styled(Box, { label: "DrawerContent" })(
  {
    padding: '32px 24px 24px 24px',
    width: '100%',
    flex: 1,
  }
);

interface SidePanelProps {
  logo?: string;
  showCabinetLogo?: boolean;
  name: string | ReactNode;
  description: string | ReactNode;
  durationText: string;
  participantLength?: number;
  leaders?: PublicLeader[];
  showSkeletons?: boolean;
  meetingId?: number;
  slotSelected?: BookingSlot;
  locations?: ReactNode[];
  conferences?: {
    provider: number;
    label: string;
  }[];
}

export const SidePanel = ({
  logo, showCabinetLogo, name, description, durationText, participantLength, leaders, showSkeletons, meetingId,
  slotSelected, locations, conferences
}: SidePanelProps): ReactElement => {
  const [open, setOpen] = useState(true);
  const [showAllLeaders, setShowAllLeaders] = useState(false);

  const date = slotSelected ? 
    DateTime.fromISO(slotSelected.start).toFormat('MMM d, yyyy') : '';
  const startTime = slotSelected ? 
    DateTime.fromISO(slotSelected.start).toFormat('h:mm a') : '';
  const endTime = slotSelected ?
    DateTime.fromISO(slotSelected.end).toFormat('h:mm a') : '';
  
  return (
    <Drawer
      variant="permanent"
      anchor="left"
      open={open}
      onClose={() => setOpen(false)}
      sx={{ width: SIDE_PANEL_WIDTH, maxWidth: 285, flexShrink: 0, '& .MuiDrawer-paper': { position: 'relative' } }}
    >
      <DrawerContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%"
          }}>
          <Box
            sx={{
              marginTop: 1,
              display: "flex",
              gap: 2.5,
              flexDirection: "column"
            }}>
            {showSkeletons ? (
              <>
                <Skeleton variant='rounded' width={120} height={30} />
                <Skeleton width='100%' height={30} variant='rounded' />
                <Box
                  sx={{
                    gap: .5,
                    display: "flex",
                    flexWrap: "wrap"
                  }}>
                  <Skeleton variant='rounded' width={120} height={30} />
                  <Skeleton variant='rounded' width={120} height={30} />
                </Box>
              </>
            ) : (
              <>
                {logo && (
                  <Box component='img' src={logo} alt='Cabinet' sx={{
                    width: 110
                  }} />
                )}

                <Typography variant="h1">{name}</Typography>
                
                {startTime && date && (
                  <Box>
                    <Typography variant="body1" color={colors.calmGrey600} sx={{
                      fontWeight: 700
                    }}>
                      {date}
                    </Typography>
                    <Typography variant="body1" color={colors.calmGrey500}>
                      {`${startTime} - ${endTime}`}
                    </Typography>
                  </Box>
                )}
                
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center"
                  }}>
                  <Typography variant="body1" color={colors.calmGrey600} sx={{
                    fontWeight: 700
                  }}>Duration:</Typography>
                  <Typography variant="body1" color={colors.calmGrey500}>{durationText}</Typography>
                </Box>
                
                {!!conferences?.length && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexDirection: "column"
                    }}>
                    {conferences.map(conference => (
                      <CabTooltip title={conference.label} key={conference.provider} wrapWithSpan>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center"
                          }}>
                          <CabIcon
                            Icon={IoVideocam}
                            color={colors.calmGrey500}
                            sx={{ fontSize: 20 }}
                          />
                          <Typography variant="body1" color={colors.calmGrey500}
                            noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {conference.label}
                          </Typography>
                        </Box>
                      </CabTooltip>
                    ))}
                  </Box>
                )}

                {!!locations?.length && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexDirection: "column"
                    }}>
                    {locations.map((location, index) => (
                      <CabTooltip title={location} key={index} wrapWithSpan>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center"
                          }}>
                          <CabIcon
                            Icon={IoLocation}
                            color={colors.calmGrey500}
                            sx={{ fontSize: 20 }}
                          />
                          <Typography variant="body1" color={colors.calmGrey500}
                            noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {location}
                          </Typography>
                        </Box>
                      </CabTooltip>
                    ))}
                  </Box>
                )}

                {participantLength !== undefined && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center"
                    }}>
                    <Typography variant="body1" color={colors.calmGrey600} sx={{
                      fontWeight: 700
                    }}>Participants:</Typography>
                    <Typography variant="body1" color={colors.calmGrey500}>{participantLength}</Typography>
                  </Box>
                )}
                
                {leaders && (
                  <Box>
                    <Divider />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        marginTop: 2,
                        maxHeight: 170,
                        overflow: "auto",

                        '::-webkit-scrollbar': {
                          width: 8, height: 8, backgroundColor: colors.white900,
                        },

                        '::-webkit-scrollbar-thumb': {
                          background: colors.black300, borderRadius: 4,
                        }
                      }}>
                      {leaders.slice(
                        0,
                        !showAllLeaders && leaders.length > DEFAULT_LEADERS_TO_SHOW ? DEFAULT_LEADERS_TO_SHOW
                          : leaders.length
                      ).map((leader, index) => (
                        <Box
                          key={leader.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            width: '100%'
                          }}>
                          <CabAvatar
                            name={`${leader.first_name} ${leader.last_name}`}
                            src={leader.pic_url}
                            color={leader.color}
                            sx={{ width: 30, height: 30, fontSize: 14, flexShrink: 0 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <CabTooltip title={`${leader.first_name} ${leader.last_name}`} key={leader.id} wrapWithSpan>
                              <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {`${leader.first_name} ${leader.last_name}`}
                              </Typography>
                              <Typography variant="body2" color={colors.calmGrey600} noWrap
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {leader.role}
                              </Typography>
                            </CabTooltip>
                          </Box>
                          {index === DEFAULT_LEADERS_TO_SHOW - 1
                            && !showAllLeaders
                            && leaders.length > DEFAULT_LEADERS_TO_SHOW && (
                            <Typography
                              variant="body2"
                              color={colors.calmGrey600}
                              sx={{ ml: 'auto', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                              +{leaders.length - DEFAULT_LEADERS_TO_SHOW} more
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                    {leaders.length > DEFAULT_LEADERS_TO_SHOW && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.black700,
                          cursor: 'pointer',
                          mt: 1
                        }}
                        onClick={() => setShowAllLeaders(!showAllLeaders)}
                      >
                        {showAllLeaders ? 'Show less' : 'View all'}
                      </Typography>
                    )}
                  </Box>
                )}

                <Divider />

                <Typography
                  component="div"
                  variant="body1"
                  color={colors.black700}
                  sx={{
                    whiteSpace: 'pre-wrap',
                    maxHeight: 200,
                    overflow: 'auto',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    paddingRight: 1,

                    '::-webkit-scrollbar': {
                      width: 8, height: 8, backgroundColor: colors.white900,
                    },

                    '::-webkit-scrollbar-thumb': {
                      background: colors.black300, borderRadius: 4,
                    }
                  }}>
                  {description}
                </Typography>
              </>
            )}
          </Box>
        
          {showCabinetLogo && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}>
              <Typography variant="caption" sx={{
                fontSize: 12
              }}>Powered by</Typography>
              <Box component='img' src={NAVY_LOGO_SRC} alt='Cabinet' sx={{
                width: 110
              }} />
            </Box>
          )}
        </Box>
      </DrawerContent>
    </Drawer>
  );
};
